// src/components/Enroll.jsx
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import "./Enroll.css";
import JoinCourseModal from "./JoinCourseModal";

const Enroll = () => {
  const [expandedModule, setExpandedModule] = useState(1);
  const [showAllWeeks, setShowAllWeeks] = useState(false);
  const location = useLocation();
  const courseData = location.state?.course;
  const [selectedCourse, setSelectedCourse] = useState(null);

  const handleEnrollClick = () => setSelectedCourse(courseData);

  // ✅ No resolveSlug needed => no ESLint deps warning
  const slug = courseData?.slug || "default";

  // -----------------------------
  // COURSE DEFINITIONS (Duration + Weeks)
  // -----------------------------
  const COURSE_META = useMemo(
    () => ({
      "advanced-ai-agentic-systems": {
        weeks: 14,
        durationLabel: "3–3.5 Months",
        titleFallback: "Advanced AI & Agentic Systems",
      },
      "data-engineering-analytics": {
        weeks: 16,
        durationLabel: "3.5–4 Months",
        titleFallback: "Advanced Data Engineering & Analytics",
      },

      // ✅ handle BOTH slugs (old + current)
      "ml-deep-learning-sprint": {
        weeks: 10,
        durationLabel: "2–2.5 Months",
        titleFallback: "Advanced ML & Deep Learning Bootcamp",
      },
      "ml-deep-learning": {
        weeks: 10,
        durationLabel: "2–2.5 Months",
        titleFallback: "Advanced ML & Deep Learning Bootcamp",
      },

      // ✅ handle BOTH slugs (old + current)
      "python-data-engineering-bootcamp": {
        weeks: 8,
        durationLabel: "1.5–2 Months",
        titleFallback: "Python + Data Engineering Bootcamp",
      },
      "python-data-engineering": {
        weeks: 8,
        durationLabel: "1.5–2 Months",
        titleFallback: "Python + Data Engineering Bootcamp",
      },

      "sql-data-analysis": {
        weeks: 6,
        durationLabel: "1–1.5 Months",
        titleFallback: "SQL Mastery & Advanced Data Analysis",
      },

      "gen-ai-professional": {
        weeks: 24,
        durationLabel: "24 Weeks (6 Months)",
        titleFallback: "Complete 6-Month Curriculum (Flagship)",
      },

      default: {
        weeks: 24,
        durationLabel: "24 Weeks",
        titleFallback: "Program Curriculum",
      },
    }),
    []
  );

  const meta = COURSE_META[slug] || COURSE_META.default;

  // -----------------------------
  // WEEK DATA
  // -----------------------------

  // 1) Advanced AI & Agentic System Program (14 weeks)
  const AI_AGENTIC_WEEKS = useMemo(
    () => [
      { id: 1, week: 1, title: "ML + DL FOUNDATION", subtitle: "TensorFlow / PyTorch", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "🧠", title: "CraftingBrain: ML/DL Fundamentals (TF + PyTorch)", locked: false },
        { type: "TRACK", icon: "🏠", title: "iNikola: Smart Home Automation Architecture (overview)", locked: false },
      ]},
      { id: 2, week: 2, title: "MODEL TRAINING", subtitle: "Evaluation + Deployment Mindset", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "📈", title: "CraftingBrain: Training, metrics, validation strategies", locked: true },
        { type: "TRACK", icon: "📡", title: "iNikola: Sensor data basics + pipeline overview", locked: true },
      ]},
      { id: 3, week: 3, title: "NLP FOUNDATIONS", subtitle: "Transformers intro", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "📝", title: "CraftingBrain: NLP basics + Transformers overview", locked: true },
        { type: "TRACK", icon: "🧹", title: "iNikola: Sensor data cleaning + signal basics", locked: true },
      ]},
      { id: 4, week: 4, title: "BERT + GPT", subtitle: "Practical NLP", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "🤖", title: "CraftingBrain: BERT / GPT usage + fine-tuning concepts", locked: true },
        { type: "TRACK", icon: "📊", title: "iNikola: Signal processing fundamentals", locked: true },
      ]},
      { id: 5, week: 5, title: "MLOPS + CLOUD", subtitle: "CI/CD mindset + logging", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "☁️", title: "CraftingBrain: MLOps & Cloud Technologies (deployments)", locked: true },
        { type: "TRACK", icon: "📟", title: "iNikola: Edge AI intro (Raspberry Pi / microcontrollers)", locked: true },
      ]},
      { id: 6, week: 6, title: "AGENTIC AI", subtitle: "Autonomy + tools", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "🧩", title: "CraftingBrain: Agentic AI concepts (tools, planning)", locked: true },
        { type: "TRACK", icon: "🔌", title: "iNikola: Edge deployment patterns (latency, constraints)", locked: true },
      ]},
      { id: 7, week: 7, title: "MULTI-AGENT SYSTEMS", subtitle: "Coordination patterns", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "👥", title: "CraftingBrain: Multi-agent systems fundamentals", locked: true },
        { type: "TRACK", icon: "🏡", title: "iNikola: Multi-agent smart environment systems", locked: true },
      ]},
      { id: 8, week: 8, title: "REAL-WORLD DEPLOYMENTS", subtitle: "Production thinking", category: "agentic-systems", lessons: [
        { type: "TRACK", icon: "🚀", title: "CraftingBrain: Real-world AI deployments", locked: true },
        { type: "TRACK", icon: "🔮", title: "iNikola: AI-based behavior prediction", locked: true },
      ]},
      { id: 9, week: 9, title: "MINI PROJECTS", subtitle: "Portfolio foundation", category: "project", lessons: [
        { type: "PROJECT", icon: "✅", title: "Mini Projects (start: 8–10 total across program)", locked: true },
        { type: "PROJECT", icon: "📦", title: "GitHub portfolio structuring (repo standards)", locked: true },
      ]},
      { id: 10, week: 10, title: "INDUSTRY PROJECTS", subtitle: "Major project kickoff", category: "project", lessons: [
        { type: "PROJECT", icon: "🏗️", title: "Major Industry Project 1 (planning + architecture)", locked: true },
        { type: "PROJECT", icon: "🧪", title: "Testing + evaluation pipelines", locked: true },
      ]},
      { id: 11, week: 11, title: "INDUSTRY PROJECTS", subtitle: "Build sprint", category: "project", lessons: [
        { type: "PROJECT", icon: "⚙️", title: "Major Industry Project 1 (implementation sprint)", locked: true },
        { type: "PROJECT", icon: "☁️", title: "Cloud-hosted deployment (first release)", locked: true },
      ]},
      { id: 12, week: 12, title: "MAJOR PROJECTS", subtitle: "2–4 projects", category: "project", lessons: [
        { type: "PROJECT", icon: "🏆", title: "Major Industry Projects (4–5 total) — build phase", locked: true },
        { type: "PROJECT", icon: "📡", title: "Edge AI deployment project (Raspberry Pi/micro)", locked: true },
      ]},
      { id: 13, week: 13, title: "FINALIZATION", subtitle: "Portfolio ready", category: "final", lessons: [
        { type: "DELIVERABLE", icon: "🧾", title: "Portfolio-ready GitHub + cloud hosted demos", locked: true },
        { type: "DELIVERABLE", icon: "📄", title: "Internship certificate + project documentation", locked: true },
      ]},
      { id: 14, week: 14, title: "INTERVIEW + PLACEMENT", subtitle: "Career readiness", category: "final", lessons: [
        { type: "CAREER", icon: "🎯", title: "Resume + portfolio guidance", locked: true },
        { type: "CAREER", icon: "💼", title: "Placement support + iNikola opportunities", locked: true },
      ]},
    ],
    []
  );

  // 2) Advanced Data Engineering & Analytics Program (16 weeks)
  const DE_ANALYTICS_WEEKS = useMemo(
    () => [
      { id: 1, week: 1, title: "PYTHON PROGRAMMING", subtitle: "Fundamentals", category: "python", lessons: [
        { type: "TRACK", icon: "🐍", title: "CraftingBrain: Python Fundamentals + OOP basics", locked: false },
        { type: "TRACK", icon: "🏠", title: "iNikola: AI-powered Automation Fundamentals", locked: false },
      ]},
      { id: 2, week: 2, title: "PYTHON + APIS", subtitle: "Automation Ready", category: "python", lessons: [
        { type: "TRACK", icon: "🌐", title: "CraftingBrain: Flask/FastAPI + automation approach", locked: true },
        { type: "TRACK", icon: "📡", title: "iNikola: Sensor Data Analysis (intro)", locked: true },
      ]},
      { id: 3, week: 3, title: "DATA ENGINEERING", subtitle: "ETL, Pipelines", category: "data-engineering", lessons: [
        { type: "TRACK", icon: "🔄", title: "CraftingBrain: ETL + pipelines foundation", locked: true },
        { type: "TRACK", icon: "🎛️", title: "iNikola: Signal Processing for Automation (intro)", locked: true },
      ]},
      { id: 4, week: 4, title: "PIPELINES", subtitle: "AWS/GCP/BigQuery", category: "data-engineering", lessons: [
        { type: "TRACK", icon: "☁️", title: "CraftingBrain: AWS/GCP + BigQuery pipelines", locked: true },
        { type: "TRACK", icon: "🧹", title: "iNikola: Data cleaning patterns for sensor streams", locked: true },
      ]},
      { id: 5, week: 5, title: "SQL ADVANCED", subtitle: "Optimization", category: "sql", lessons: [
        { type: "TRACK", icon: "🗄️", title: "CraftingBrain: Advanced SQL queries + optimization", locked: true },
        { type: "TRACK", icon: "📊", title: "iNikola: Time-series slicing & aggregation", locked: true },
      ]},
      { id: 6, week: 6, title: "SQL + ANALYTICS", subtitle: "Real datasets", category: "sql", lessons: [
        { type: "TRACK", icon: "📈", title: "CraftingBrain: Analytics queries + KPI extraction", locked: true },
        { type: "TRACK", icon: "⚙️", title: "iNikola: Automation insights from sensor data", locked: true },
      ]},
      { id: 7, week: 7, title: "DATA ANALYSIS", subtitle: "EDA + Reporting", category: "machine-learning", lessons: [
        { type: "TRACK", icon: "📊", title: "CraftingBrain: EDA + ML intro for analytics", locked: true },
        { type: "TRACK", icon: "🧪", title: "iNikola: Signal-based feature extraction", locked: true },
      ]},
      { id: 8, week: 8, title: "ML FOR ANALYTICS", subtitle: "Supervised + Unsupervised", category: "machine-learning", lessons: [
        { type: "TRACK", icon: "🧠", title: "CraftingBrain: ML for business analytics", locked: true },
        { type: "TRACK", icon: "🔮", title: "iNikola: Behavior patterns (intro models)", locked: true },
      ]},
      { id: 9, week: 9, title: "DATA VISUALIZATION", subtitle: "Tableau", category: "machine-learning", lessons: [
        { type: "TRACK", icon: "📉", title: "CraftingBrain: Tableau dashboards", locked: true },
        { type: "TRACK", icon: "🧰", title: "iNikola: Dashboarding for automation metrics", locked: true },
      ]},
      { id: 10, week: 10, title: "PIPELINE PROJECTS", subtitle: "End-to-End", category: "project", lessons: [
        { type: "PROJECT", icon: "🏗️", title: "Data pipelines (AWS/GCP/BigQuery)", locked: true },
        { type: "PROJECT", icon: "📦", title: "End-to-end data engineering project", locked: true },
      ]},
      { id: 11, week: 11, title: "ANALYTICS PROJECTS", subtitle: "Dashboards", category: "project", lessons: [
        { type: "PROJECT", icon: "📊", title: "Analytics dashboards build sprint", locked: true },
        { type: "PROJECT", icon: "✅", title: "Portfolio shaping (GitHub + deployment)", locked: true },
      ]},
      { id: 12, week: 12, title: "INDUSTRY PROJECTS", subtitle: "Major build", category: "project", lessons: [
        { type: "PROJECT", icon: "🏆", title: "Major industry project build phase", locked: true },
        { type: "PROJECT", icon: "🧪", title: "Testing + data quality checks", locked: true },
      ]},
      { id: 13, week: 13, title: "OPTIMIZATION", subtitle: "Performance + cost", category: "project", lessons: [
        { type: "PROJECT", icon: "⚡", title: "Query optimization + pipeline cost control", locked: true },
        { type: "PROJECT", icon: "🔍", title: "Monitoring + alerts", locked: true },
      ]},
      { id: 14, week: 14, title: "FINALIZATION", subtitle: "Documentation", category: "final", lessons: [
        { type: "DELIVERABLE", icon: "🧾", title: "Project documentation + dashboard walkthrough", locked: true },
        { type: "DELIVERABLE", icon: "☁️", title: "Cloud-hosted demo", locked: true },
      ]},
      { id: 15, week: 15, title: "INTERNSHIP WRAP-UP", subtitle: "Certificates", category: "final", lessons: [
        { type: "CAREER", icon: "🎓", title: "Internship certificate + completion certificate", locked: true },
        { type: "CAREER", icon: "📄", title: "Resume & portfolio polishing", locked: true },
      ]},
      { id: 16, week: 16, title: "PLACEMENT SUPPORT", subtitle: "Interview prep", category: "final", lessons: [
        { type: "CAREER", icon: "🎯", title: "Mock interviews + job opportunities", locked: true },
        { type: "CAREER", icon: "💼", title: "iNikola opportunities & referral support", locked: true },
      ]},
    ],
    []
  );

  // 3) Advanced ML & Deep Learning Bootcamp (10 weeks)
  const ML_DL_WEEKS = useMemo(
    () => [
      { id: 1, week: 1, title: "ML CORE", subtitle: "Supervised Learning", category: "machine-learning", lessons: [
        { type: "TOPIC", icon: "🎯", title: "ML Algorithms (Supervised & Unsupervised)", locked: false },
      ]},
      { id: 2, week: 2, title: "DEEP LEARNING", subtitle: "ANN", category: "neural-networks", lessons: [
        { type: "TOPIC", icon: "🧠", title: "Deep Learning (ANN) + implementation", locked: true },
      ]},
      { id: 3, week: 3, title: "DEEP LEARNING", subtitle: "CNN", category: "neural-networks", lessons: [
        { type: "TOPIC", icon: "👁️", title: "CNN + practical implementation", locked: true },
      ]},
      { id: 4, week: 4, title: "DEEP LEARNING", subtitle: "RNN/LSTM", category: "neural-networks", lessons: [
        { type: "TOPIC", icon: "🔄", title: "RNN, LSTM for sequences", locked: true },
      ]},
      { id: 5, week: 5, title: "MODEL TRAINING", subtitle: "Evaluation", category: "machine-learning", lessons: [
        { type: "TOPIC", icon: "📈", title: "Model Training & Evaluation", locked: true },
      ]},
      { id: 6, week: 6, title: "DEPLOYMENTS", subtitle: "Real-world ML", category: "project", lessons: [
        { type: "TOPIC", icon: "🚀", title: "Real-world ML deployments", locked: true },
      ]},
      { id: 7, week: 7, title: "AUTOMATION MODELS", subtitle: "AI-driven automation", category: "project", lessons: [
        { type: "TOPIC", icon: "🤖", title: "AI-driven automation models", locked: true },
      ]},
      { id: 8, week: 8, title: "PROJECTS", subtitle: "Build Sprint", category: "project", lessons: [
        { type: "PROJECT", icon: "🏗️", title: "Mini projects + portfolio structure", locked: true },
      ]},
      { id: 9, week: 9, title: "PROJECTS", subtitle: "Major Project", category: "project", lessons: [
        { type: "PROJECT", icon: "🏆", title: "Major project build + testing", locked: true },
      ]},
      { id: 10, week: 10, title: "FINISH", subtitle: "Portfolio + Career", category: "final", lessons: [
        { type: "CAREER", icon: "🎯", title: "Resume + placement support", locked: true },
      ]},
    ],
    []
  );

  // 4) Python + Data Engineering Bootcamp (8 weeks)
  const PY_DE_WEEKS = useMemo(
    () => [
      { id: 1, week: 1, title: "PYTHON FUNDAMENTALS", subtitle: "Basics", category: "python", lessons: [
        { type: "TOPIC", icon: "🐍", title: "Python Fundamentals & OOP", locked: false },
      ]},
      { id: 2, week: 2, title: "APIS", subtitle: "Flask/FastAPI", category: "python", lessons: [
        { type: "TOPIC", icon: "🌐", title: "APIs using Flask/FastAPI", locked: true },
      ]},
      { id: 3, week: 3, title: "AUTOMATION", subtitle: "Selenium", category: "data-engineering", lessons: [
        { type: "TOPIC", icon: "🤖", title: "Automation using Selenium", locked: true },
      ]},
      { id: 4, week: 4, title: "DATA ENGINEERING", subtitle: "Basics", category: "data-engineering", lessons: [
        { type: "TOPIC", icon: "🔄", title: "Data Engineering Basics", locked: true },
      ]},
      { id: 5, week: 5, title: "SQL + VIZ", subtitle: "Analytics", category: "sql", lessons: [
        { type: "TOPIC", icon: "🗄️", title: "SQL & Data Visualization", locked: true },
      ]},
      { id: 6, week: 6, title: "CLOUD DEPLOYMENT", subtitle: "AWS", category: "project", lessons: [
        { type: "TOPIC", icon: "☁️", title: "Cloud deployment (AWS)", locked: true },
      ]},
      { id: 7, week: 7, title: "PROJECT", subtitle: "Implementation", category: "project", lessons: [
        { type: "PROJECT", icon: "🏗️", title: "Project build sprint", locked: true },
      ]},
      { id: 8, week: 8, title: "WRAP-UP", subtitle: "Portfolio", category: "final", lessons: [
        { type: "CAREER", icon: "📦", title: "Portfolio-ready GitHub + hosted project", locked: true },
      ]},
    ],
    []
  );

  // 5) SQL Bootcamp (6 weeks)
  const SQL_WEEKS = useMemo(
    () => [
      { id: 1, week: 1, title: "SQL FUNDAMENTALS", subtitle: "Core queries", category: "sql", lessons: [
        { type: "TOPIC", icon: "🗄️", title: "SQL basics + CRUD", locked: false },
      ]},
      { id: 2, week: 2, title: "JOINS", subtitle: "Relationships", category: "sql", lessons: [
        { type: "TOPIC", icon: "🔗", title: "Joins + real datasets", locked: true },
      ]},
      { id: 3, week: 3, title: "SUBQUERIES", subtitle: "Advanced queries", category: "sql", lessons: [
        { type: "TOPIC", icon: "🔍", title: "Subqueries + correlated queries", locked: true },
      ]},
      { id: 4, week: 4, title: "ANALYTICAL FUNCTIONS", subtitle: "Window functions", category: "sql", lessons: [
        { type: "TOPIC", icon: "📊", title: "Analytical functions + optimization", locked: true },
      ]},
      { id: 5, week: 5, title: "DATA ANALYSIS", subtitle: "Stats + reporting", category: "machine-learning", lessons: [
        { type: "TOPIC", icon: "📈", title: "Statistical analysis + reporting", locked: true },
        { type: "TOPIC", icon: "📉", title: "Visualization (Tableau/Matplotlib/Seaborn)", locked: true },
      ]},
      { id: 6, week: 6, title: "FINAL PROJECT", subtitle: "Portfolio", category: "final", lessons: [
        { type: "PROJECT", icon: "🏆", title: "SQL + analysis portfolio project", locked: true },
      ]},
    ],
    []
  );

  // 6) Flagship 24-week curriculum
  const FLAGSHIP_24_WEEKS = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= 24; i++) {
      arr.push({
        id: i,
        week: i,
        title: `WEEK ${i}`,
        subtitle: "Flagship Curriculum",
        liveSessions: 3,
        assignments: 2,
        category: "final",
        lessons: [
          { type: "LIVE LESSON", icon: "📘", title: "Flagship curriculum content", locked: i !== 1 },
        ],
      });
    }
    return arr;
  }, []);

  // -----------------------------
  // PICK DATASET BY COURSE SLUG
  // -----------------------------
  const allModuleData = useMemo(() => {
    switch (slug) {
      case "advanced-ai-agentic-systems":
        return AI_AGENTIC_WEEKS;
      case "data-engineering-analytics":
        return DE_ANALYTICS_WEEKS;
      case "ml-deep-learning-sprint":
      case "ml-deep-learning":
        return ML_DL_WEEKS;
      case "python-data-engineering-bootcamp":
      case "python-data-engineering":
        return PY_DE_WEEKS;
      case "sql-data-analysis":
        return SQL_WEEKS;
      case "gen-ai-professional":
      default:
        return FLAGSHIP_24_WEEKS;
    }
  }, [
    slug,
    AI_AGENTIC_WEEKS,
    DE_ANALYTICS_WEEKS,
    ML_DL_WEEKS,
    PY_DE_WEEKS,
    SQL_WEEKS,
    FLAGSHIP_24_WEEKS,
  ]);

  const totalModules = meta.weeks;

  const moduleData = useMemo(() => {
    const list = allModuleData;
    return showAllWeeks ? list : list.slice(0, 4);
  }, [allModuleData, showAllWeeks]);

  const toggleModule = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  return (
    <div className="course-enrollment">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              {courseData?.title || meta.titleFallback} <br />
              <span className="gradient-text">{meta.durationLabel}</span>
            </h1>

            <br />

            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">{totalModules}</span>
                <span className="stat-label">Weeks</span>
              </div>
              <div className="stat">
                <span className="stat-number">{totalModules * 3}</span>
                <span className="stat-label">Live Sessions</span>
              </div>
              <div className="stat">
                <span className="stat-number">{Math.max(4, Math.round(totalModules / 2))}</span>
                <span className="stat-label">Inikola Classes</span>
              </div>
              <div className="stat">
                <span className="stat-number">
                  {slug === "advanced-ai-agentic-systems" ? "4–5" : "2+"}
                </span>
                <span className="stat-label">Major Projects</span>
              </div>
            </div>
          </div>

          <div className="enrollment-card">
            <div className="price-section">
              <div className="completion-rate">
                <span className="percentage">95%</span>
                <span className="completion-text">Student Success Rate</span>
              </div>
              <div className="course-subtitle">Proven Learning System</div>
              <div className="highlight-badge">STRUCTURED CURRICULUM</div>
            </div>

            <div className="course-features">
              {/* ✅ Removed batch start date as requested */}
              <div className="feature">
                <span className="feature-icon">⏱️</span>
                <span>Duration: {courseData?.duration || meta.durationLabel}</span>
              </div>
              <div className="feature">
                <span className="feature-icon">🎓</span>
                <span>Industry Expert Mentorship</span>
              </div>
              <div className="feature">
                <span className="feature-icon">💼</span>
                <span>Portfolio Projects</span>
              </div>
            </div>

            <button className="enroll-btn" onClick={handleEnrollClick} type="button">
              <span>Enroll Now</span>
              <span className="btn-icon">→</span>
            </button>

            <div className="guarantee">
              <span className="guarantee-icon">🛡️</span>
              <span>30-day money-back guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section className="curriculum-section">
        <div className="curriculum-container">
          <div className="curriculum-header">
            <h2 className="section-title">
              Curriculum Overview <span className="gradient-text">({meta.durationLabel})</span>
            </h2>
            <p className="section-subtitle">
              Project-Focused Learning with hands-on implementation
            </p>
          </div>

          <div className="timeline-container">
            <div className="timeline-line"></div>

            {moduleData.map((module) => (
              <div key={module.id} className="module-container">
                <div className="week-indicator">
                  <div className="week-number">WEEK {module.week}</div>
                  <div className="week-dot"></div>
                </div>

                <div className="module-card">
                  <div className="module-header" onClick={() => toggleModule(module.id)}>
                    <div className="module-icon">
                      <div className="icon-circle">
                        {module.category === "python"
                          ? "🐍"
                          : module.category === "data-engineering"
                          ? "⚙️"
                          : module.category === "sql"
                          ? "🗄️"
                          : module.category === "machine-learning"
                          ? "🧠"
                          : module.category === "neural-networks"
                          ? "🤖"
                          : module.category === "nlp"
                          ? "📝"
                          : module.category === "transformers"
                          ? "🔄"
                          : module.category === "rag-langchain"
                          ? "⛓️"
                          : module.category === "agentic-systems"
                          ? "👥"
                          : module.category === "project"
                          ? "🗂️"
                          : "🎓"}
                      </div>
                    </div>

                    <div className="module-info">
                      <div className="module-label">MODULE {module.week}</div>
                      <div className="module-title-section">
                        <h3 className="module-title">{module.title}</h3>
                        <h4 className="module-subtitle">{module.subtitle}</h4>
                      </div>
                    </div>

                    <div className="module-stats">
                      {!!module.liveSessions && (
                        <div className="stat-chip live">
                          <span className="stat-dot"></span>
                          <span>{module.liveSessions} LIVE SESSIONS</span>
                        </div>
                      )}
                      {!!module.assignments && (
                        <div className="stat-chip assignment">
                          <span className="stat-dot"></span>
                          <span>{module.assignments} ASSIGNMENTS</span>
                        </div>
                      )}
                      <div className="expand-icon">
                        {expandedModule === module.id ? "⌄" : "⌃"}
                      </div>
                    </div>
                  </div>

                  {expandedModule === module.id && (
                    <div className="module-content">
                      {module.lessons.map((lesson, index) => (
                        <div key={index} className="lesson-item">
                          <div className="lesson-icon">
                            <span>{lesson.icon}</span>
                          </div>
                          <div className="lesson-info">
                            <div className="lesson-type">{lesson.type}</div>
                            <div className="lesson-title">{lesson.title}</div>
                          </div>
                          <div className="lesson-status">
                            {lesson.locked ? (
                              <div className="lock-icon">🔒</div>
                            ) : (
                              <div className="unlock-icon">✓</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {allModuleData.length > 4 && (
              <div className="show-more-container">
                <button
                  className="show-more-btn"
                  onClick={() => setShowAllWeeks(!showAllWeeks)}
                  type="button"
                >
                  {showAllWeeks ? (
                    <>
                      <span>Show Less</span>
                      <span className="btn-icon">→</span>
                    </>
                  ) : (
                    <>
                      <span>Show All {allModuleData.length} Weeks</span>
                      <span className="btn-icon">↓</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="benefits-section">
        <div className="benefits-container">
          <h2 className="section-title">
            What You <span className="gradient-text">Get</span>
          </h2>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">💡</div>
              <h3>Hands-on Skills</h3>
              <p>Practical learning with real implementation</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🤝</div>
              <h3>Mentorship</h3>
              <p>Direct guidance from AI experts & engineers</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💼</div>
              <h3>Projects</h3>
              <p>Portfolio-ready GitHub + cloud-hosted deployments</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🎓</div>
              <h3>Certificates</h3>
              <p>Internship + course completion certificates</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">☁️</div>
              <h3>Cloud Support</h3>
              <p>Free cloud credits (AWS/GCP/Azure) + LMS tracking</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🎯</div>
              <h3>Placement Support</h3>
              <p>Resume + portfolio guidance + job opportunities</p>
            </div>
          </div>
        </div>
      </section>

      <JoinCourseModal
        isOpen={!!selectedCourse}
        onClose={() => setSelectedCourse(null)}
        course={selectedCourse}
      />
    </div>
  );
};

export default Enroll;
